import React, { useCallback, useState } from 'react';
import { isNil } from 'lodash';

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import {
  DateField,
  RecordContextProvider,
  SelectInput,
  usePermissions,
} from 'react-admin';
import TransactionSummaryTable from '../../transaction/TransactionSummaryTable';
import currencies from '../../../constants/currencies';
import DailySum from '../../transaction/tabs/DailySum';
import SubNavigation from '../../layout/SubNavigation';
import { StyledTab, StyledTabs } from '../../layout/Tab';
import { ValueOf } from '../../../utilities/types';
import RollingSum from '../../transaction/tabs/RollingSum';
import SmartDateInput from '../../../customFields/SmartDateInput';
import EntityDisplayNameField from '../../../customFields/EntityDisplayNameField';
import ReferenceEntityField from '../../../customFields/ReferenceEntityField';
import hasAccess from '../../../utilities/hasAccess';
import actions, { Actions } from '../../../constants/actions';
import SnapshotDeleteButton from './SnapshotDeleteButton';
import Snapshot from '../../../utilities/schemas/Snapshot';
import ReferenceMemberField from '../../../customFields/ReferenceMemberField';

const tabs = {
  DAILY_SUM: 'dailySum',
  ROLLING_SUM: 'rollingSum',
} as const;
type TabValues = ValueOf<typeof tabs>;

const SnapshotComponent = ({ snapshot }: {snapshot: Snapshot}) => {
  const [tab, setTab] = useState<TabValues>(tabs.DAILY_SUM);

  const methods = useForm({ values: { selectedRow: snapshot.transactionTable.rowToShowInGraph } });
  const handleTabChange = useCallback((
    _event: React.SyntheticEvent,
    newValue: ValueOf<typeof tabs>,
  ) => {
    setTab(newValue);
  }, []);
  const { permissions } = usePermissions<Actions[]>();

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <Card variant="outlined">
        <CardHeader
          title={(
            <RecordContextProvider value={snapshot}>
              <ReferenceEntityField label="Snapshot" source="" idKey="entityId" entityType={snapshot.entityType} linkType={false}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap={1}>
                  <Typography>Snapshot:</Typography>
                  <EntityDisplayNameField />
                </Box>
              </ReferenceEntityField>
            </RecordContextProvider>
          )}
          subheader={(
            <RecordContextProvider value={snapshot}>
              <Box display="flex" flexDirection="row" alignItems="baseline" gap={1}>
                <Typography>Saved on</Typography>
                <DateField source="createdAt" />
                <Typography>by</Typography>
                <ReferenceMemberField noLink />
              </Box>
            </RecordContextProvider>
          )}
          action={
            hasAccess(permissions, actions.CASE_TRANSACTION_SNAPSHOT_DELETE)
              && (<SnapshotDeleteButton snapshotId={snapshot.id} />)
          }
        />
        <Divider />
        <CardContent>
          <Box display="flex" gap={4} flexWrap="wrap" alignItems="baseline">
            <RecordContextProvider value={snapshot}>
              <SelectInput
                label="Currency"
                source="currency"
                choices={currencies.map((currency) => ({
                  id: currency,
                  name: currency,
                }))}
                disabled
              />
              <SmartDateInput
                source="from"
                label="From"
                disabled
              />
              <SmartDateInput
                source="to"
                label="To"
                disabled
              />
            </RecordContextProvider>
          </Box>
          <TransactionSummaryTable
            params={{
              from: snapshot.from.toString(),
              to: snapshot.to.toString(),
              currency: snapshot.currency,
              rollingSumWindow: snapshot.window,
              distributionWindow: snapshot.window,
              entityId: snapshot.entityId,
              entityType: snapshot.entityType,
              flowPageSize: 0,
            }}
            filter={{
              from: snapshot.from.toString(),
              to: snapshot.to.toString(),
              currency: snapshot.currency,
              entityId: snapshot.entityId,
              entityType: snapshot.entityType,
              requestType: 'transaction',
            }}
            preferenceKey={`${snapshot.id}`}
            showExpectedBehaviourInput
            showExpectedBehaviour={snapshot.showExpectedBehaviour}
            expectedBehaviourCache={snapshot.expectedBehaviour}
            shouldDisableInput
            defaultConfiguration={{
              id: snapshot.id,
              rowToShowInGraph: snapshot.transactionTable.rowToShowInGraph ? `${snapshot.transactionTable.rowToShowInGraph}-${snapshot.id}` : '',
              rows: snapshot.transactionTable.tableData
                .filter((data) => !isNil(data.transactionTypeConfiguration))
                .map(({ transactionTypeConfiguration }) => {
                  if (!transactionTypeConfiguration) throw new Error('No transactionType config');
                  return ({
                    transactionTypeConfiguration,
                    id: `${JSON.stringify(transactionTypeConfiguration)}-${snapshot.id}`,
                  });
                }),
              columns: snapshot.transactionTable.tableColumns,
              entityType: snapshot.entityType,
            }}
            demo={{
              demoData: snapshot.transactionTable.tableData.map((tableData, index) => ({
                data: {
                  ...tableData.data,
                  id: `${tableData.transactionTypeConfiguration}-${index}`,
                },
                isLoading: false,
              })),
              addRow: () => {},
              deleteRow: () => {},
            }}
          />
          <Box marginTop={6}>
            <SubNavigation>
              <StyledTabs value={tab} onChange={handleTabChange}>
                <StyledTab label="Daily sum" value={tabs.DAILY_SUM} />
                <StyledTab label="Rolling sum" value={tabs.ROLLING_SUM} />
              </StyledTabs>
            </SubNavigation>
            {tab === tabs.DAILY_SUM
                && (
                  <DailySum
                    filter={{
                      from: snapshot.from.toString(),
                      to: snapshot.to.toString(),
                      currency: snapshot.currency,
                      entityId: snapshot.entityId,
                      entityType: snapshot.entityType,
                      requestType: 'transaction',
                      transactionTypeConfiguration: snapshot.transactionTable.rowToShowInGraph,
                    }}
                    data={snapshot.dailySumGraphData}
                    expectedBehaviour={snapshot.expectedBehaviour}
                    showExpectedBehaviour={snapshot.showExpectedBehaviour}
                  />
                )}
            {tab === tabs.ROLLING_SUM
                && (
                  <RollingSum
                    filter={{
                      from: snapshot.from.toString(),
                      to: snapshot.to.toString(),
                      currency: snapshot.currency,
                      entityId: snapshot.entityId,
                      entityType: snapshot.entityType,
                      requestType: 'transaction',
                      transactionTypeConfiguration: snapshot.transactionTable.rowToShowInGraph,
                    }}
                    data={snapshot.rollingSumGraphData}
                    params={{
                      from: snapshot.from.toString(),
                      to: snapshot.to.toString(),
                      currency: snapshot.currency,
                      rollingSumWindow: snapshot.window,
                      distributionWindow: snapshot.window,
                      entityId: snapshot.entityId,
                      entityType: snapshot.entityType,
                      flowPageSize: 0,
                    }}
                    setParams={() => {}}
                    disableInput
                    expectedBehaviour={snapshot.expectedBehaviour}
                    showExpectedBehaviour={snapshot.showExpectedBehaviour}
                  />
                )}
          </Box>
        </CardContent>
      </Card>
    </FormProvider>
  );
};

export default SnapshotComponent;
